import React, { useMemo, useState } from "react";
import { ArtifactProto } from "build-ts/chat_session";
import { ArtifactType } from "build-ts/common";
import { KGDataFrameProto } from "build-ts/dataframe";
import { populateArtifact } from "services/DataService";
import DataFrameDisplay from "../components/DataFrame";

interface DataFrameContainerProps {
  name?: string;
  data: KGDataFrameProto;
  isSelected?: boolean;
  onClose?: () => void;
  /** No-op in the panel context — collapse is handled by the artifact strip. */
  collapseSignal?: number;
  /** Whether the artifact panel is currently expanded to full width */
  isExpanded?: boolean;
  /** Called when the expand/collapse toggle button is clicked */
  onExpandToggle?: () => void;
}

/**
 * Container for KGDataFrame artifacts.
 * Owns the displayed data state and the recompute service call (populateArtifact).
 * Expression filter toggles are also managed here since they drive the service call.
 */
const DataFrameContainer: React.FC<DataFrameContainerProps> = ({
  name,
  data,
  isExpanded,
  onExpandToggle,
}) => {
  const [displayedData, setDisplayedData] = useState<KGDataFrameProto>(data);
  const [isRecomputing, setIsRecomputing] = useState(false);

  // Tracks which expression filters are currently checked (all checked by default)
  const [checkedExpressionFilters, setCheckedExpressionFilters] = useState<
    Record<string, boolean>
  >(() => {
    const initial: Record<string, boolean> = {};
    (data.expression_filters ?? []).forEach((filter) => {
      initial[filter] = true;
    });
    return initial;
  });

  // Tracks which filters were active at the time of the last successful fetch —
  // used to detect whether the current selection has drifted from the displayed data.
  const [lastFetchedFilters, setLastFetchedFilters] = useState<string[]>(
    data.expression_filters ?? []
  );

  const hasExpressionFiltersChanged = useMemo(() => {
    const current = Object.entries(checkedExpressionFilters)
      .filter(([, isChecked]) => isChecked)
      .map(([filter]) => filter);
    if (current.length !== lastFetchedFilters.length) return true;
    const lastSet = new Set(lastFetchedFilters);
    return current.some((f) => !lastSet.has(f));
  }, [checkedExpressionFilters, lastFetchedFilters]);

  const handleExpressionFilterToggle = (filter: string) => {
    setCheckedExpressionFilters((prev) => ({ ...prev, [filter]: !prev[filter] }));
  };

  const handleRecompute = async () => {
    const selectedFilters = Object.entries(checkedExpressionFilters)
      .filter(([, isChecked]) => isChecked)
      .map(([filter]) => filter);

    const modifiedData: KGDataFrameProto = { ...data, expression_filters: selectedFilters };
    const artifactRequest = ArtifactProto.create({
      artifact_type: ArtifactType.KG_DATAFRAME,
      kg_dataframe: modifiedData,
    });

    setIsRecomputing(true);
    try {
      const response = await populateArtifact(artifactRequest);
      const dataframeData = response?.kg_dataframe;
      if (dataframeData) {
        setDisplayedData(dataframeData);
        setLastFetchedFilters(selectedFilters);
      }
    } finally {
      setIsRecomputing(false);
    }
  };

  return (
    <DataFrameDisplay
      name={name}
      data={data}
      displayedData={displayedData}
      checkedExpressionFilters={checkedExpressionFilters}
      isRecomputing={isRecomputing}
      hasExpressionFiltersChanged={hasExpressionFiltersChanged}
      onExpressionFilterToggle={handleExpressionFilterToggle}
      onRecompute={handleRecompute}
      isExpanded={isExpanded}
      onExpandToggle={onExpandToggle}
    />
  );
};

export default DataFrameContainer;
